/**
 * ENG-5750 — register/unregister the impersonation "introduce yourself"
 * SessionStart hook in the operator's project dir.
 *
 * `connect` calls `registerIntroduceHook(projectCwd)` after scaffolding
 * CLAUDE.md/.mcp.json. It upserts a SessionStart (matcher `startup`) hook
 * into `<projectCwd>/.claude/settings.local.json` that shells out to
 * `agt impersonate introduce` on every fresh Claude Code boot.
 *
 * The upsert mirrors `provisionOrientHook()` in @augmented/core
 * (packages/core/src/provisioning/frameworks/claudecode/index.ts): don't
 * clobber existing SessionStart entries, and stay idempotent so a repeat
 * connect doesn't stack duplicates.
 *
 * Unlike the managed-agent orient hook (which owns a long-lived
 * settings file), impersonation is ephemeral: `exit` must leave the
 * operator's cwd exactly as it found it. So registration records a
 * `HookBackup` — whether settings.local.json pre-existed (backed up vs
 * created) and whether we created the `.claude` dir — and
 * `unregisterIntroduceHook()` reverses precisely.
 */

import {
  copyFileSync,
  existsSync,
  mkdirSync,
  readdirSync,
  readFileSync,
  renameSync,
  rmdirSync,
  unlinkSync,
  writeFileSync,
} from 'node:fs';
import { join } from 'node:path';

import { BACKUP_SUFFIX } from './impersonate-fs-ops.js';
import type { HookBackup } from './impersonate-state.js';

/**
 * The command the SessionStart hook runs. Bare `agt` (PATH lookup) for
 * the same reason `connect` spawns bare `claude`: the operator just ran
 * `agt impersonate connect`, so the binary is demonstrably on PATH, and
 * the hook inherits that PATH from the launching shell. Exposed as a
 * parameter so tests can assert against a fixed value.
 */
export const INTRODUCE_HOOK_COMMAND = 'agt impersonate introduce';

const SESSION_START_MATCHER = 'startup';

/**
 * Upsert the introduce SessionStart hook into
 * `<projectCwd>/.claude/settings.local.json`. Returns a HookBackup the
 * manifest stores so `exit` can undo exactly what was done.
 */
export function registerIntroduceHook(
  projectCwd: string,
  command: string = INTRODUCE_HOOK_COMMAND,
): HookBackup {
  const claudeDir = join(projectCwd, '.claude');
  const createdClaudeDir = !existsSync(claudeDir);
  mkdirSync(claudeDir, { recursive: true });

  const settingsPath = join(claudeDir, 'settings.local.json');
  const settingsExisted = existsSync(settingsPath);
  const backupPath = settingsPath + BACKUP_SUFFIX;

  let settings: Record<string, unknown> = {};
  if (settingsExisted) {
    // Back up the original verbatim so exit restores it byte-for-byte,
    // then merge our hook into the parsed copy. Guard the copy: if a
    // backup already exists (a prior register that wasn't exited),
    // DON'T clobber it — otherwise exit would restore an already-hooked
    // file instead of the operator's true original. A malformed or
    // non-object JSON root is preserved as the backup but not merged
    // into (start fresh from {}).
    if (!existsSync(backupPath)) {
      copyFileSync(settingsPath, backupPath);
    }
    settings = asPlainObject(safeParseJson(readFileSync(settingsPath, 'utf-8')));
  }

  const hooks = asPlainObject(settings['hooks']);
  const existing = Array.isArray(hooks[SESSION_START_KEY])
    ? [...(hooks[SESSION_START_KEY] as Array<Record<string, unknown>>)]
    : [];

  const alreadyRegistered = existing.some((entry) =>
    entryMatchesCommand(entry, command),
  );
  if (!alreadyRegistered) {
    existing.push({
      matcher: SESSION_START_MATCHER,
      hooks: [{ type: 'command', command }],
    });
  }
  hooks[SESSION_START_KEY] = existing;
  settings['hooks'] = hooks;

  writeFileSync(settingsPath, JSON.stringify(settings, null, 2) + '\n');

  return {
    settings_path: settingsPath,
    settings_backup: settingsExisted ? 'had-file' : 'didnt-exist',
    created_claude_dir: createdClaudeDir,
  };
}

/**
 * Reverse registerIntroduceHook: restore the backed-up settings file (or
 * remove the one we created) and drop the `.claude` dir if we created it
 * and it's now empty. Best-effort and idempotent — safe to call twice.
 */
export function unregisterIntroduceHook(backup: HookBackup): void {
  const { settings_path, settings_backup, created_claude_dir } = backup;
  const backupPath = settings_path + BACKUP_SUFFIX;

  if (settings_backup === 'had-file') {
    // Restore the original over our merged version.
    if (existsSync(backupPath)) {
      renameSync(backupPath, settings_path);
    }
  } else {
    // We created it — remove it (and the stray backup, if any).
    if (existsSync(settings_path)) unlinkSync(settings_path);
    if (existsSync(backupPath)) unlinkSync(backupPath);
  }

  if (created_claude_dir) {
    const claudeDir = dirOf(settings_path);
    try {
      if (existsSync(claudeDir) && readdirSync(claudeDir).length === 0) {
        rmdirSync(claudeDir);
      }
    } catch {
      // Non-empty (operator added files) or already gone — leave it.
    }
  }
}

const SESSION_START_KEY = 'SessionStart';

function entryMatchesCommand(entry: Record<string, unknown>, command: string): boolean {
  if ((entry as { matcher?: unknown }).matcher !== SESSION_START_MATCHER) return false;
  const entryHooks = (entry as { hooks?: unknown }).hooks;
  return (
    Array.isArray(entryHooks) &&
    entryHooks.some(
      (h) =>
        typeof h === 'object' &&
        h !== null &&
        (h as { type?: unknown }).type === 'command' &&
        (h as { command?: unknown }).command === command,
    )
  );
}

function dirOf(filePath: string): string {
  const idx = filePath.lastIndexOf('/');
  return idx === -1 ? filePath : filePath.slice(0, idx);
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Coerce a parsed JSON value to a plain object, or {} for null/array/scalar. */
function asPlainObject(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
